// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createDataProvider } from '@shared/ui-kit/providers/data-provider/create-data-provider';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo, useEffect } from 'react';
import produce from 'immer';
import type {
  SourceAsset,
  TransferOperationData,
  RefundType,
  DestinationFund,
  FundSearchResult,
} from '@shared/portfolio-operations/types';
import {
  transferOperationFormSchema,
  type TransferOperationFormData,
} from '@shared/portfolio-operations/schemas/transfer-operation';
import {
  calculateSharesFromPercentage,
  calculateAmountFromPercentage,
  calculatePercentageFromShares,
  calculatePercentageFromAmount,
  calculateDestinationAmountFromPercentage,
  calculateDestinationPercentageFromAmount,
  roundDestinationPercentageInput,
  roundAmountTo2,
} from '@shared/portfolio-operations/utils/transfer-calculations';

export interface TransferOperationProviderProps {
  sourceAsset: SourceAsset;
  portfolioFunds?: FundSearchResult[];
  onFormDataChange?: (data: TransferOperationData) => void;
  onFormValidationChange?: (isValid: boolean) => void;
}

interface TransferOperationContextValue {
  sourceAsset: SourceAsset;
  portfolioFunds?: FundSearchResult[];
  form: UseFormReturn<TransferOperationFormData>;
  totalShares: number;
  totalAmount: number;
  currency: string;
  excludedTickers: string[];
  onRefundTypeChange: (type: RefundType) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onAddDestinationFund: (fund: FundSearchResult) => void;
  onDestinationFundPercentageChange: (id: string, value: number) => void;
  onDestinationFundAmountChange: (id: string, value: number) => void;
  onDestinationFundPercentageBlur: (id: string) => void;
  onDestinationFundAmountBlur: (id: string) => void;
  onRemoveDestinationFund: (id: string) => void;
  formData: TransferOperationData;
  isFormValid: boolean;
}

const useTransferOperationFactory = ({
  sourceAsset,
  portfolioFunds,
  onFormDataChange,
  onFormValidationChange,
}: TransferOperationProviderProps): TransferOperationContextValue => {
  const totalShares = sourceAsset.quantity;
  const totalAmount = sourceAsset.investment;
  const currency = sourceAsset.currency;

  const form = useForm<TransferOperationFormData>({
    resolver: zodResolver(transferOperationFormSchema),
    mode: 'onChange',
    defaultValues: {
      type: 'full',
      percentage: 100,
      shares: totalShares,
      amount: totalAmount,
      inputType: 'amount',
      destinationFunds: [],
    },
  });

  const { watch, setValue, getValues } = form;

  const refundType = watch('type');
  const refundPercentage = watch('percentage');
  const refundShares = watch('shares');
  const refundAmount = watch('amount');
  const refundInputType = watch('inputType');
  const destinationFunds = watch('destinationFunds');

  const excludedTickers = useMemo(
    () => [sourceAsset.ticker, ...destinationFunds.map(fund => fund.ticker)],
    [sourceAsset.ticker, destinationFunds],
  );

  const formData: TransferOperationData = useMemo(
    () => ({
      type: refundType,
      percentage: refundPercentage,
      shares: refundShares,
      amount: refundAmount,
      inputType: refundInputType,
      destinationFunds,
    }),
    [
      refundType,
      refundPercentage,
      refundShares,
      refundAmount,
      refundInputType,
      destinationFunds,
    ],
  );

  const onRefundTypeChange = useCallback(
    (type: RefundType) => {
      const currentFunds = getValues('destinationFunds');
      const opts = { shouldValidate: true as const };

      switch (type) {
        case 'full':
          setValue('percentage', 100, opts);
          setValue('shares', totalShares, opts);
          setValue('amount', totalAmount, opts);
          setValue(
            'destinationFunds',
            produce(currentFunds, draft => {
              const base = roundAmountTo2(totalAmount);
              draft.forEach(f => {
                f.amount = calculateDestinationAmountFromPercentage(
                  f.percentage,
                  base,
                );
              });
            }),
            opts,
          );
          setValue('type', 'full', opts);
          break;
        case 'partial': {
          const defaultPct = 50;
          const amount = calculateAmountFromPercentage(defaultPct, totalAmount);
          setValue('percentage', defaultPct, opts);
          setValue(
            'shares',
            calculateSharesFromPercentage(defaultPct, totalShares),
            opts,
          );
          setValue('amount', amount, opts);
          setValue(
            'destinationFunds',
            produce(currentFunds, draft => {
              const base = roundAmountTo2(amount);
              draft.forEach(f => {
                f.amount = calculateDestinationAmountFromPercentage(
                  f.percentage,
                  base,
                );
              });
            }),
            opts,
          );
          setValue('type', 'partial', opts);
          break;
        }
        default:
          return;
      }
    },
    [totalShares, totalAmount, setValue, getValues],
  );
  const onAddDestinationFund = useCallback(
    (fund: FundSearchResult) => {
      if (fund.ticker === sourceAsset.ticker) {
        return;
      }

      const currentFunds = getValues('destinationFunds');
      const base = refundType === 'full' ? totalAmount : refundAmount;
      const activeBaseAmount = roundAmountTo2(base);
      const totalFunds = currentFunds.length + 1;
      const percentagePerFund = 100 / totalFunds;

      const updatedFunds = produce(currentFunds, draft => {
        draft.forEach(f => {
          f.percentage = percentagePerFund;
          f.amount = calculateDestinationAmountFromPercentage(
            percentagePerFund,
            activeBaseAmount,
          );
        });
      });

      const newFund: DestinationFund = {
        id: `${fund.ticker}-${Date.now()}`,
        ticker: fund.ticker,
        name: fund.name,
        isin: fund.isin,
        percentage: percentagePerFund,
        amount: calculateDestinationAmountFromPercentage(
          percentagePerFund,
          activeBaseAmount,
        ),
        dfi_url: fund.dfi_url,
      };

      setValue('destinationFunds', [...updatedFunds, newFund], {
        shouldValidate: true,
      });
    },
    [
      setValue,
      getValues,
      sourceAsset.ticker,
      refundType,
      refundAmount,
      totalAmount,
    ],
  );

  const onDestinationFundPercentageChange = useCallback(
    (id: string, value: number) => {
      const currentFunds = getValues('destinationFunds');
      const percentageRounded = roundDestinationPercentageInput(value);
      const updatedFunds = produce(currentFunds, draft => {
        const fund = draft.find(f => f.id === id);
        if (fund) fund.percentage = percentageRounded;
      });
      setValue('destinationFunds', updatedFunds, { shouldValidate: true });
    },
    [setValue, getValues],
  );

  const onDestinationFundPercentageBlur = useCallback(
    (id: string) => {
      const currentFunds = getValues('destinationFunds');
      const base = refundType === 'full' ? totalAmount : refundAmount;
      const activeBaseAmount = roundAmountTo2(base);
      const updatedFunds = produce(currentFunds, draft => {
        const fund = draft.find(f => f.id === id);
        if (fund) {
          fund.amount = calculateDestinationAmountFromPercentage(
            fund.percentage,
            activeBaseAmount,
          );
        }
      });
      setValue('destinationFunds', updatedFunds, { shouldValidate: true });
    },
    [refundAmount, refundType, totalAmount, setValue, getValues],
  );

  const onDestinationFundAmountChange = useCallback(
    (id: string, value: number) => {
      const currentFunds = getValues('destinationFunds');
      const updatedFunds = produce(currentFunds, draft => {
        const fund = draft.find(f => f.id === id);
        if (fund) fund.amount = value;
      });
      setValue('destinationFunds', updatedFunds, { shouldValidate: true });
    },
    [setValue, getValues],
  );

  const onDestinationFundAmountBlur = useCallback(
    (id: string) => {
      const currentFunds = getValues('destinationFunds');
      const base = refundType === 'full' ? totalAmount : refundAmount;
      const activeBaseAmount = roundAmountTo2(base);
      const updatedFunds = produce(currentFunds, draft => {
        const fund = draft.find(f => f.id === id);
        if (fund) {
          fund.percentage = calculateDestinationPercentageFromAmount(
            fund.amount,
            activeBaseAmount,
          );
        }
      });
      setValue('destinationFunds', updatedFunds, { shouldValidate: true });
    },
    [refundAmount, refundType, totalAmount, setValue, getValues],
  );

  const onRemoveDestinationFund = useCallback(
    (id: string) => {
      const currentFunds = getValues('destinationFunds');

      setValue(
        'destinationFunds',
        currentFunds.filter(fund => fund.id !== id),
        { shouldValidate: true },
      );
    },
    [setValue, getValues],
  );

  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (refundType === 'full') return;

      const name = e.target.name as 'percentage' | 'shares' | 'amount';
      const opts = { shouldValidate: true as const };

      switch (name) {
        case 'percentage': {
          const percentage = getValues('percentage');
          const currentShares = getValues('shares');
          const currentAmount = getValues('amount');
          const percentageFromShares = calculatePercentageFromShares(
            currentShares,
            totalShares,
          );
          const percentageFromAmount = calculatePercentageFromAmount(
            currentAmount,
            totalAmount,
          );
          const pRounded = roundAmountTo2(percentage);
          const pFromSharesRounded = roundAmountTo2(percentageFromShares);
          const pFromAmountRounded = roundAmountTo2(percentageFromAmount);

          if (pRounded === pFromSharesRounded || pRounded === pFromAmountRounded) {
            break;
          }

          const shares = calculateSharesFromPercentage(percentage, totalShares);
          const amount = calculateAmountFromPercentage(percentage, totalAmount);
          setValue('percentage', percentage, opts);
          setValue('shares', shares, opts);
          setValue('amount', amount, opts);
          const currentFunds = getValues('destinationFunds');
          const baseAmount = roundAmountTo2(amount);
          setValue(
            'destinationFunds',
            produce(currentFunds, draft => {
              draft.forEach(fund => {
                fund.amount = calculateDestinationAmountFromPercentage(
                  fund.percentage,
                  baseAmount,
                );
              });
            }),
            opts,
          );
          break;
        }
        case 'shares': {
          const shares = getValues('shares');
          const currentPercentage = getValues('percentage');
          const currentAmount = getValues('amount');
          const sharesFromPercentage = calculateSharesFromPercentage(
            currentPercentage,
            totalShares,
          );
          const sharesFromAmount = calculateSharesFromPercentage(
            calculatePercentageFromAmount(currentAmount, totalAmount),
            totalShares,
          );
          const sRounded = roundAmountTo2(shares);
          const sFromPctRounded = roundAmountTo2(sharesFromPercentage);
          const sFromAmtRounded = roundAmountTo2(sharesFromAmount);

          if (sRounded === sFromPctRounded || sRounded === sFromAmtRounded) {
            break;
          }

          const percentage = calculatePercentageFromShares(shares, totalShares);
          const amount = calculateAmountFromPercentage(percentage, totalAmount);
          setValue('percentage', percentage, opts);
          setValue('shares', shares, opts);
          setValue('amount', amount, opts);
          setValue('inputType', 'shares', opts);
          const currentFunds = getValues('destinationFunds');
          const baseAmount = roundAmountTo2(amount);
          setValue(
            'destinationFunds',
            produce(currentFunds, draft => {
              draft.forEach(fund => {
                fund.amount = calculateDestinationAmountFromPercentage(
                  fund.percentage,
                  baseAmount,
                );
              });
            }),
            opts,
          );
          break;
        }
        case 'amount': {
          const amount = getValues('amount');
          const currentShares = getValues('shares');
          const amountFromShares = calculateAmountFromPercentage(
            calculatePercentageFromShares(currentShares, totalShares),
            totalAmount,
          );
          const amountRounded = roundAmountTo2(amount);
          const amountFromSharesRounded = roundAmountTo2(amountFromShares);

          if (amountRounded === amountFromSharesRounded) {
            break;
          }

          const percentage = calculatePercentageFromAmount(amount, totalAmount);
          const shares = calculateSharesFromPercentage(percentage, totalShares);
          setValue('percentage', percentage, opts);
          setValue('shares', shares, opts);
          setValue('amount', amount, opts);
          setValue('inputType', 'amount', opts);
          const currentFunds = getValues('destinationFunds');
          const baseAmount = roundAmountTo2(amount);
          setValue(
            'destinationFunds',
            produce(currentFunds, draft => {
              draft.forEach(fund => {
                fund.amount = calculateDestinationAmountFromPercentage(
                  fund.percentage,
                  baseAmount,
                );
              });
            }),
            opts,
          );
          break;
        }
        default:
          return;
      }
    },
    [refundType, totalShares, totalAmount, setValue, getValues],
  );

  useEffect(() => {
    onFormDataChange?.(formData);
  }, [formData, onFormDataChange]);

  useEffect(() => {
    onFormValidationChange?.(form.formState.isValid);
  }, [form.formState.isValid, onFormValidationChange]);

  return {
    sourceAsset,
    portfolioFunds,
    form,
    totalShares,
    totalAmount,
    currency,
    excludedTickers,
    onRefundTypeChange,
    onBlur,
    onAddDestinationFund,
    onDestinationFundPercentageChange,
    onDestinationFundAmountChange,
    onDestinationFundPercentageBlur,
    onDestinationFundAmountBlur,
    onRemoveDestinationFund,
    formData,
    isFormValid: form.formState.isValid,
  };
};

export const { TransferOperationProvider, useTransferOperationData } =
  createDataProvider({
    name: 'TransferOperation',
    factory: useTransferOperationFactory,
  });
